import { useState, type FormEvent } from 'react';
import {
  EVENT_COLORS,
  RECUR_FREQS,
  minutesToLabel,
  eventColor,
  type EventCategory,
  type EventInstance,
  type RecurFreq,
} from '@td/shared';
import { useEvent, useCreateEvent, useUpdateEvent, useDeleteEvent } from '../hooks/useEvents';
import { useCategories } from '../hooks/useCategories';
import { ApiRequestError } from '../lib/api';
import { Modal } from './Modal';

/** 15-minute steps across the day. */
const SLOTS = Array.from({ length: 96 }, (_, i) => i * 15);

interface Props {
  /** An existing occurrence to edit, or a date to create on. */
  instance: EventInstance | null;
  date: string;
  onClose: () => void;
}

export function EventEditor({
  instance,
  date,
  onClose,
  onManageCategories,
}: Props & { onManageCategories?: () => void }) {
  const existing = useEvent(instance?.seriesId ?? null);
  const { categories, all: allCategories } = useCategories();
  const create = useCreateEvent();
  const update = useUpdateEvent();
  const remove = useDeleteEvent();

  const source = existing.data;
  const [title, setTitle] = useState(instance?.title ?? '');
  const [location, setLocation] = useState(instance?.location ?? '');
  const [description, setDescription] = useState(instance?.description ?? '');
  const [when, setWhen] = useState(instance?.date ?? date);
  const [allDay, setAllDay] = useState(instance?.allDay ?? false);
  const [start, setStart] = useState(instance?.startMinutes ?? 9 * 60);
  const [end, setEnd] = useState(instance?.endMinutes ?? 10 * 60);
  const [category, setCategory] = useState<EventCategory>(instance?.category ?? 'school');
  const [color, setColor] = useState<string | null>(instance?.color ?? null);
  const [freq, setFreq] = useState<RecurFreq>(source?.recurrence.freq ?? 'none');
  const [until, setUntil] = useState(source?.recurrence.until ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!allDay && end < start) {
      setError('The end time is before the start time.');
      return;
    }

    setBusy(true);
    const payload = {
      title: title.trim(),
      date: when,
      allDay,
      startMinutes: start,
      endMinutes: end,
      category,
      color,
      location: location.trim() || null,
      description: description.trim() || null,
      recurrence: { freq, interval: 1, until: freq === 'none' ? null : until || null },
    };

    try {
      if (instance) await update.mutateAsync({ id: instance.seriesId, ...payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not save that event.');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(scope: 'occurrence' | 'series') {
    if (!instance) return;
    setBusy(true);
    try {
      await remove.mutateAsync({ id: instance.seriesId, scope, date: instance.date });
      onClose();
    } catch {
      setError('Could not delete that event.');
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Event"
      className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-4 text-lg">{instance ? 'Edit event' : 'New event'}</h2>

      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="label" htmlFor="ev-title">
            Title
          </label>
          <input
            id="ev-title"
            className="field"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Grade-level PLC"
            maxLength={200}
            required
            autoFocus
          />
        </div>

        <div>
          <label className="label" htmlFor="ev-date">
            Date
          </label>
          <input
            id="ev-date"
            type="date"
            className="field"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            required
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-walnut">
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            className="h-4 w-4 accent-terracotta"
          />
          All day
        </label>

        {!allDay && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="ev-start">
                Starts
              </label>
              <select
                id="ev-start"
                className="field"
                value={start}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setStart(v);
                  if (end < v) setEnd(Math.min(v + 60, 1440));
                }}
              >
                {SLOTS.map((m) => (
                  <option key={m} value={m}>
                    {minutesToLabel(m)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="ev-end">
                Ends
              </label>
              <select
                id="ev-end"
                className="field"
                value={end}
                onChange={(e) => setEnd(Number(e.target.value))}
              >
                {SLOTS.filter((m) => m >= start).map((m) => (
                  <option key={m} value={m}>
                    {minutesToLabel(m)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <fieldset>
          <legend className="label">Category</legend>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                className={[
                  'flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition-colors',
                  category === c.key
                    ? 'border-clay bg-linen text-espresso'
                    : 'border-sand text-walnut hover:bg-linen',
                ].join(' ')}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                {c.label}
              </button>
            ))}
            {onManageCategories && (
              <button
                type="button"
                onClick={onManageCategories}
                className="rounded-lg border border-dashed border-clay px-2.5 py-1 text-xs text-walnut hover:bg-linen"
              >
                + New
              </button>
            )}
          </div>
        </fieldset>

        <fieldset>
          <legend className="label">
            Colour <span className="text-stone">(optional)</span>
          </legend>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setColor(null)}
              title="Use the category colour"
              aria-label="Use the category colour"
              aria-pressed={color === null}
              className={[
                'flex h-7 items-center rounded-lg border px-2 text-[11px] transition-colors',
                color === null
                  ? 'border-espresso text-espresso'
                  : 'border-sand text-stone hover:bg-linen',
              ].join(' ')}
            >
              Category
            </button>
            {EVENT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                title={c}
                aria-label={`Use colour ${c}`}
                aria-pressed={color === c}
                className={[
                  'h-7 w-7 rounded-lg border-2 transition-transform',
                  color === c ? 'border-espresso scale-110' : 'border-transparent hover:scale-105',
                ].join(' ')}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-stone">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: eventColor({ color, category }, allCategories) }}
            />
            This is how it will show on the calendar
          </div>
        </fieldset>

        <div>
          <label className="label" htmlFor="ev-loc">
            Location <span className="text-stone">(optional)</span>
          </label>
          <input
            id="ev-loc"
            className="field"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Room 12"
            maxLength={200}
          />
        </div>

        <div>
          <label className="label" htmlFor="ev-repeat">
            Repeats
          </label>
          <select
            id="ev-repeat"
            className="field"
            value={freq}
            onChange={(e) => setFreq(e.target.value as RecurFreq)}
          >
            {RECUR_FREQS.map((f) => (
              <option key={f} value={f}>
                {f === 'none' ? 'Does not repeat' : f}
              </option>
            ))}
          </select>
        </div>

        {freq !== 'none' && (
          <div>
            <label className="label" htmlFor="ev-until">
              Until <span className="text-stone">(optional)</span>
            </label>
            <input
              id="ev-until"
              type="date"
              className="field"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        )}

        <div>
          <label className="label" htmlFor="ev-notes">
            Notes <span className="text-stone">(optional)</span>
          </label>
          <textarea
            id="ev-notes"
            className="field min-h-[64px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
          />
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <div className="flex gap-2">
            {instance && (
              <>
                {instance.isRecurring && (
                  <button
                    type="button"
                    onClick={() => void onDelete('occurrence')}
                    className="text-xs text-walnut hover:text-rust"
                  >
                    Delete this one
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void onDelete('series')}
                  className="text-xs text-rust hover:underline"
                >
                  {instance.isRecurring ? 'Delete all' : 'Delete'}
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
